import { useMemo } from 'react';

import Debug from 'HHC/Debug';
import { isNumber, isString, treeToKeys } from 'Utils/Utils';
import getFieldRules, { Rules } from 'src/components/VacancyCreate/utils/getFieldRules';
import { useSelector } from 'src/hooks/useSelector';

/*
    Валидатор возвращает значение, приводимое к true,
    если значение валидно. Валидатор получает параметры:
    rule -- значение правила из объекта, передаваемого в useRulesValidator
    { value, allValues } -- значение валидируемого поля и значение всех полей формы.
    Может быть асинхронным.
 */

enum ValidationRule {
    Required = 'required',
    MinLength = 'minLength',
    MaxLength = 'maxLength',
    AlwaysInvalid = 'alwaysInvalid',
    Regexp = 'regexp',
    MinValue = 'minValue',
    MaxValue = 'maxValue',
    MinCount = 'minCount',
    MaxCount = 'maxCount',
}

interface ValidationPropsType<FieldValue> {
    value: FieldValue;
    allValues: unknown;
}

type ValidatorType<FieldValue> = (rule: unknown, { value, allValues }: ValidationPropsType<FieldValue>) => boolean;

const isValidationRule = (rule: unknown): rule is ValidationRule => Object.values(ValidationRule).includes(rule);

const logWrongValidatorType = (
    validationRule: ValidationRule,
    expectedValueTypes: string[],
    value: unknown,
    expectedRuleType: string,
    rule: unknown
) => {
    Debug.log(
        'out error',
        new Error(
            `Wrong rule or value type for validation rule ${validationRule}. Expected one of ${expectedValueTypes.toString()} for value, got ${typeof value}. Expected ${expectedRuleType} for rule, got ${typeof rule}. Actual field value is ${String(
                value
            )}, actual rule value is ${String(rule)}`
        )
    );
};

const validators = new Map<ValidationRule, ValidatorType<unknown>>();

/*
    Валидаторы в порядке приоритета. Выводится ошибка от того, который находится выше.
    Асинхронные учитываются только если в синхронных ошибок нет.
 */
validators.set(ValidationRule.Required, (rule, { value }) => !rule || value !== undefined);
validators.set(ValidationRule.MinLength, (rule, { value }) => {
    if (!value) {
        return true;
    }

    if ((isString(value) || Array.isArray(value)) && isNumber(rule)) {
        return value.length >= rule;
    }

    logWrongValidatorType(ValidationRule.MinLength, ['string', 'array'], value, 'number', rule);
    return false;
});
validators.set(ValidationRule.AlwaysInvalid, (rule) => !rule);
validators.set(ValidationRule.Regexp, (rule, { value }) => {
    if (!value) {
        return true;
    }

    if (isString(rule) && isString(value)) {
        return RegExp(rule, 'i').test(value);
    }

    logWrongValidatorType(ValidationRule.Regexp, ['string'], value, 'string', rule);
    return false;
});
validators.set(ValidationRule.MinValue, (rule, { value }) => {
    if (!value) {
        return true;
    }

    if ((isNumber(value) || isString(value)) && isNumber(rule)) {
        const numericValue = parseInt(value.toString(), 10);
        return (!numericValue && numericValue !== 0) || numericValue >= rule;
    }

    logWrongValidatorType(ValidationRule.MinValue, ['string', 'number'], value, 'number', rule);
    return false;
});
validators.set(ValidationRule.MaxValue, (rule, { value }) => {
    if (!value) {
        return true;
    }

    if ((isNumber(value) || isString(value)) && isNumber(rule)) {
        return !parseInt(value.toString(), 10) || parseInt(value.toString(), 10) <= rule;
    }

    logWrongValidatorType(ValidationRule.MaxValue, ['string', 'number'], value, 'number', rule);
    return false;
});
validators.set(ValidationRule.MinCount, (rule, { value }) => {
    if (!value) {
        return true;
    }

    if (Array.isArray(value) && isNumber(rule)) {
        return value.length >= rule;
    }

    logWrongValidatorType(ValidationRule.MinCount, ['array'], value, 'number', rule);
    return false;
});
validators.set(ValidationRule.MaxCount, (rule, { value }) => {
    if (!value) {
        return true;
    }

    if (Array.isArray(value) && isNumber(rule)) {
        return value.length <= rule;
    }

    logWrongValidatorType(ValidationRule.MaxCount, ['array'], value, 'number', rule);
    return false;
});
validators.set(ValidationRule.MaxLength, (rule, { value }) => {
    if (!value) {
        return true;
    }

    if ((isString(value) || Array.isArray(value)) && isNumber(rule)) {
        return value.length <= rule;
    }

    logWrongValidatorType(ValidationRule.MinLength, ['string', 'array'], value, 'number', rule);
    return false;
});

const callValidator = <FieldValue>(
    key: ValidationRule,
    rule: unknown,
    validationProps: ValidationPropsType<FieldValue>
) => {
    const result = validators.get(key)?.(rule, validationProps);
    return !result ? key : null;
};

const findFirstError = (array: (ValidationRule | null)[]) => {
    return array.find((result) => result);
};

export function validateFieldByRules<FieldValue>(rules: Rules) {
    const flattedRules = treeToKeys(rules);
    const orderedRuleKeys: ValidationRule[] = [];

    const ruleKeys = Object.keys(flattedRules).filter((key) => isValidationRule(key) && validators.has(key));

    validators.forEach((_value, key) => {
        if (ruleKeys.includes(key)) {
            orderedRuleKeys.push(key);
        }
    });

    return (value: FieldValue, allValues: object) => {
        const validationResults = orderedRuleKeys.map((key) =>
            callValidator(key, flattedRules[key], { value, allValues })
        );
        return findFirstError(validationResults);
    };
}

function useFieldRulesValidator<FieldValue>({
    customRules = {},
    fieldName,
}: {
    customRules?: Rules;
    fieldName?: string;
}) {
    const vacancyCreateRules = useSelector((state) => state.vacancyCreateRules);

    const rules = useMemo(() => {
        return (fieldName && { ...getFieldRules(fieldName, vacancyCreateRules), ...customRules }) || customRules;
    }, [customRules, fieldName, vacancyCreateRules]);

    const _validateFieldByRules = useMemo(() => {
        return validateFieldByRules<FieldValue>(rules);
    }, [rules]);

    return { rules, validateFieldByRules: _validateFieldByRules };
}

export default useFieldRulesValidator;
